/**
 * Facebook Conversions API Client-Side Utilities
 * Handles cookie extraction and API communication from the browser
 */

/**
 * Generate a unique event ID for deduplication between Pixel and CAPI
 * Format: timestamp_randomString
 */
export function generateEventId(): string {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  return `${timestamp}_${randomString}`;
}

/**
 * Get a cookie value by name
 */
function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  }
  
  return undefined;
}

/**
 * Get Facebook Browser Pixel cookie (_fbp)
 * This cookie is automatically set by the Facebook Pixel
 */
export function getFbp(): string | undefined {
  return getCookie('_fbp');
}

/**
 * Get Facebook Click ID cookie (_fbc)
 * This cookie is set when a user arrives via a Facebook ad
 */
export function getFbc(): string | undefined {
  return getCookie('_fbc');
}

/**
 * Send an event to the Facebook Conversions API via our backend
 */
interface SendEventToAPIParams {
  eventName: 'PageView' | 'ViewContent' | 'AddToCart' | 'InitiateCheckout' | 'Purchase';
  slug: string;
  eventId?: string;
  contentName?: string;
  contentCategory?: string;
  contentIds?: string[];
  value?: number;
  currency?: string;
  numItems?: number;
}

export async function sendEventToAPI(params: SendEventToAPIParams): Promise<{success: boolean; error?: string}> {
  try {
    const eventSourceUrl = typeof window !== 'undefined' ? window.location.href : '';
    
    const response = await fetch('/api/facebook-capi', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventName: params.eventName,
        eventSourceUrl,
        eventId: params.eventId,
        fbp: getFbp(),
        fbc: getFbc(),
        slug: params.slug,
        contentName: params.contentName,
        contentCategory: params.contentCategory,
        contentIds: params.contentIds,
        value: params.value,
        currency: params.currency,
        numItems: params.numItems,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Facebook CAPI Client Error:', data);
      return {
        success: false,
        error: data.error || 'Failed to send event'
      };
    }

    return {success: true};
  } catch (error) {
    console.error('Facebook CAPI Client Network Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Send a PageView event to the API
 */
export async function sendPageViewEvent(slug: string): Promise<void> {
  try {
    await sendEventToAPI({
      eventName: 'PageView',
      slug,
    });
  } catch (error) {
    console.error('Error sending PageView event:', error);
  }
}

/**
 * Send a ViewContent event to the API
 */
export async function sendViewContentEvent(params: {
  slug: string;
  contentName: string;
  contentCategory?: string;
  contentIds?: string[];
  value?: number;
  currency?: string;
}): Promise<void> {
  try {
    await sendEventToAPI({
      eventName: 'ViewContent',
      ...params,
    });
  } catch (error) {
    console.error('Error sending ViewContent event:', error);
  }
}

/**
 * Send an AddToCart event to the API
 */
export async function sendAddToCartEvent(params: {
  slug: string;
  contentName: string;
  contentIds?: string[];
  value: number;
  currency: string;
}): Promise<void> {
  try {
    await sendEventToAPI({
      eventName: 'AddToCart',
      ...params,
    });
  } catch (error) {
    console.error('Error sending AddToCart event:', error);
  }
}

/**
 * Send an InitiateCheckout event to the API
 */
export async function sendInitiateCheckoutEvent(params: {
  slug: string;
  value: number;
  currency: string;
  numItems: number;
}): Promise<void> {
  try {
    await sendEventToAPI({
      eventName: 'InitiateCheckout',
      ...params,
    });
  } catch (error) {
    console.error('Error sending InitiateCheckout event:', error);
  }
}

/**
 * Send a Purchase event to the API
 */
export async function sendPurchaseEvent(params: {
  slug: string;
  value: number;
  currency: string;
  numItems: number;
  contentIds?: string[];
}): Promise<void> {
  try {
    await sendEventToAPI({
      eventName: 'Purchase',
      ...params,
    });
  } catch (error) {
    console.error('Error sending Purchase event:', error);
  }
}
